import { useLingui } from '@lingui/react';
import { useMutation } from '@tanstack/react-query';

import { useIsAuth } from '#modules/user/features/auth/hooks/useIsAuth';
import { envConfig } from '#constants/envConfig';
import { locales } from '../constants/locales';
import { changeProfileLocale } from '../model/change-locale';
import { buildLocaleSwitchPath } from '../utils';
import { setLocaleCookie, setNativeLocale } from '../utils/nativeLocale';
import { updateLocale } from '../utils/updateLocale';

export const useLanguageSwitcher = () => {
  const changeProfileLocaleMutation = useMutation({
    mutationFn: changeProfileLocale,
  });
  const isAuth = useIsAuth();

  const changeLocale = async (newLocale: keyof typeof locales) => {
    if (!Object.keys(locales).includes(newLocale)) return;

    // Persist the choice (cookie + profile) before reloading.
    if (envConfig.IS_NATIVE) {
      // `updateLocale` is a server function and there is no server inside the app —
      // calling it threw here and aborted the switch before the navigation below, so
      // tapping a language did nothing at all. Write the same cookie from the client
      // and mirror it into native storage so the choice survives a restart.
      setLocaleCookie(newLocale);
      await setNativeLocale(newLocale);
    } else {
      await updateLocale({ data: newLocale });
    }

    if (isAuth) {
      // Best-effort: losing the profile-side preference must not block the switch.
      try {
        await changeProfileLocaleMutation.mutateAsync();
      } catch {
        // keep going — the cookie already carries the choice
      }
    }

    // Build the target URL explicitly (swap the locale prefix) and hard-reload.
    // The server resolves the locale from the URL path first, so the prefix must
    // actually change — navigate({ params: { locale } }) doesn't reliably drop
    // the existing /ru prefix when switching to the default locale, leaving the
    // page on Russian.
    const newPath = buildLocaleSwitchPath(window.location.pathname, newLocale);
    window.location.assign(newPath + window.location.search + window.location.hash);
  };

  const { i18n } = useLingui();

  return { currentLocale: i18n.locale as keyof typeof locales, changeLocale };
};
